import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { CffListRow } from '../../components/forms/CffListRow';
import { ActionMenuButton } from '../../components/ui/ActionMenuButton';
import { PageHeader } from '../../components/ui/PageHeader';
import { PageToolbar } from '../../components/ui/PageToolbar';
import { Modal } from '../../components/ui/Modal';
import { VerifyingOverlay } from '../../components/ui/VerifyingOverlay';
import { PageNotificationToast } from '../../components/notifications/PageNotificationToast';
import { fetchCustomerFeedbackSubmissions, cffWarehouseRolesStatusLabel, cffWarehouseRolesStatus } from '../../lib/customerFeedbackSubmissions';
import {
  cffYearMonthFromApplicationNo,
  currentCffYearMonth,
  downloadCffMasterListExcel,
  downloadMonthlyCffExcel,
} from '../../lib/customerFeedbackExcel';
import { STATUS_LABEL, formatCffSubmittedByLabel } from '../../lib/accountCreationUi';
import { usePortal } from '../../contexts/PortalContext';
import { useFormsSubmissionsRealtime } from '../../hooks/useFormsSubmissionsRealtime';
import { usePageNotifications } from '../../hooks/usePageNotifications';
import { pageFeedbackToToast } from '../../lib/pageFeedbackToast';
import type { ApplicationSubmission, CustomerFeedbackFormData } from '../../types/applicationForm';
import { formatSingaporeDateTime } from '../../utils/dateTime';

const MONTH_VALUES = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'] as const;

function parseYearMonth(yearMonth: string): { year: string; month: string } {
  const m = /^(\d{4})-(\d{2})$/.exec(yearMonth.trim());
  if (m) return { year: m[1], month: m[2] };
  const fallback = currentCffYearMonth();
  return { year: fallback.slice(0, 4), month: fallback.slice(5, 7) };
}

function monthShortLabel(month: string): string {
  const date = new Date(`2020-${month}-15T12:00:00+08:00`);
  return new Intl.DateTimeFormat('en-SG', {
    month: 'short',
    timeZone: 'Asia/Singapore',
  }).format(date);
}

function monthLabel(yearMonth: string): string {
  const { year, month } = parseYearMonth(yearMonth);
  return `${monthShortLabel(month)}'${year}`;
}

function requestTypeLabel(type: CustomerFeedbackFormData['request_type']): string {
  if (type === 'return') return 'Return';
  if (type === 'exchange') return 'Exchange';
  if (type === 'replenish') return 'Replenish';
  return '—';
}

function itemCodesLabel(data: CustomerFeedbackFormData | undefined): string {
  const codes = (data?.items ?? [])
    .map((row) => String(row.item_code ?? '').trim())
    .filter(Boolean);
  return codes.length > 0 ? codes.join(', ') : '—';
}

function cffCreatorLabel(row: ApplicationSubmission): string {
  const data = row.form_data as CustomerFeedbackFormData;
  return data?.submitted_by?.trim() || row.submitted_by?.trim() || 'Unknown';
}

type CffPersonMonthStat = {
  person: string;
  total: number;
};

function buildCffPersonMonthStats(monthRows: ApplicationSubmission[]): CffPersonMonthStat[] {
  const map = new Map<string, number>();
  for (const row of monthRows) {
    const person = cffCreatorLabel(row);
    map.set(person, (map.get(person) ?? 0) + 1);
  }
  return [...map.entries()]
    .map(([person, total]) => ({ person, total }))
    .sort(
      (a, b) => b.total - a.total || a.person.localeCompare(b.person, undefined, { sensitivity: 'base' }),
    );
}

function cffRowSearchBlob(row: ApplicationSubmission): string {
  const data = row.form_data as CustomerFeedbackFormData;
  const submittedBy = formatCffSubmittedByLabel(
    data?.submitted_by?.trim() || row.submitted_by,
    data?.invoice_issued_by,
  );
  return [
    row.application_no,
    row.company_name,
    data?.company_code,
    requestTypeLabel(data?.request_type || ''),
    data?.request_type,
    data?.invoice_no,
    itemCodesLabel(data),
    data?.reason,
    data?.internal_remarks,
    data?.picker,
    data?.packer,
    data?.driver,
    cffWarehouseRolesStatusLabel(cffWarehouseRolesStatus(data)),
    data?.invoice_issued_by,
    data?.submitted_by,
    submittedBy,
    row.submitted_by,
    row.status,
    STATUS_LABEL[row.status],
  ]
    .map((v) => String(v ?? '').toLowerCase())
    .join('\n');
}

type PeriodPickersProps = {
  year: string;
  month: string;
  yearOptions: string[];
  countsByYm: Map<string, number>;
  disabled?: boolean;
  yearAriaLabel: string;
  monthAriaLabel: string;
  onYearChange: (year: string) => void;
  onMonthChange: (month: string) => void;
};

function CffPeriodPickers({
  year,
  month,
  yearOptions,
  countsByYm,
  disabled,
  yearAriaLabel,
  monthAriaLabel,
  onYearChange,
  onMonthChange,
}: PeriodPickersProps) {
  return (
    <div className="cff-page__period">
      <label className="cff-page__month-field">
        <span>Year</span>
        <select
          className="field-input cff-page__year-select"
          value={year}
          disabled={disabled}
          onChange={(e) => onYearChange(e.target.value)}
          aria-label={yearAriaLabel}
        >
          {yearOptions.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </label>
      <label className="cff-page__month-field">
        <span>Month</span>
        <select
          className="field-input cff-page__month-select"
          value={month}
          disabled={disabled}
          onChange={(e) => onMonthChange(e.target.value)}
          aria-label={monthAriaLabel}
        >
          {MONTH_VALUES.map((mm) => {
            const count = countsByYm.get(`${year}-${mm}`) ?? 0;
            return (
              <option key={mm} value={mm}>
                {monthShortLabel(mm)}
                {count > 0 ? ` (${count})` : ''}
              </option>
            );
          })}
        </select>
      </label>
    </div>
  );
}

export function CustomerFeedbackListPage() {
  const { basePath, kind } = usePortal();
  const [rows, setRows] = useState<ApplicationSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportOverlay, setExportOverlay] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [yearMonth, setYearMonth] = useState(currentCffYearMonth);
  const [search, setSearch] = useState('');
  const [statsOpen, setStatsOpen] = useState(false);
  const lastErrorToastRef = useRef<string | null>(null);
  const lastNoticeToastRef = useRef<string | null>(null);
  const { push: pushToast, activeToast, dismissToast } = usePageNotifications();

  useEffect(() => {
    if (error && error !== lastErrorToastRef.current) {
      lastErrorToastRef.current = error;
      pushToast(pageFeedbackToToast({ kind: 'error', message: error }));
    }
    if (!error) lastErrorToastRef.current = null;
  }, [error, pushToast]);

  useEffect(() => {
    if (notice && notice !== lastNoticeToastRef.current) {
      lastNoticeToastRef.current = notice;
      pushToast(pageFeedbackToToast({ kind: 'success', message: notice }));
    }
    if (!notice) lastNoticeToastRef.current = null;
  }, [notice, pushToast]);

  const listBase = basePath ? `${basePath}/customer-feedback` : '/customer-feedback';
  const newPath = `${listBase}/new`;
  const homePath = basePath || '/forms';
  const { year, month } = parseYearMonth(yearMonth);

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = Boolean(opts?.silent);
    if (!silent) setLoading(true);
    setError(null);
    try {
      setRows(await fetchCustomerFeedbackSubmissions());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load forms.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useFormsSubmissionsRealtime({
    tables: ['customer_feedback_submissions'],
    onChange: () => {
      void load({ silent: true });
    },
  });

  const countsByYm = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of rows) {
      const ym = cffYearMonthFromApplicationNo(row.application_no);
      if (!ym) continue;
      map.set(ym, (map.get(ym) ?? 0) + 1);
    }
    return map;
  }, [rows]);

  const yearOptions = useMemo(() => {
    const set = new Set<string>();
    for (const ym of countsByYm.keys()) set.add(ym.slice(0, 4));
    set.add(currentCffYearMonth().slice(0, 4));
    set.add(year);
    return [...set].sort((a, b) => b.localeCompare(a));
  }, [countsByYm, year]);

  const setYear = useCallback((nextYear: string) => {
    setYearMonth((prev) => `${nextYear}-${parseYearMonth(prev).month}`);
  }, []);

  const setMonth = useCallback((nextMonth: string) => {
    setYearMonth((prev) => `${parseYearMonth(prev).year}-${nextMonth}`);
  }, []);

  const monthRows = useMemo(
    () =>
      rows.filter((row) => cffYearMonthFromApplicationNo(row.application_no) === yearMonth),
    [rows, yearMonth],
  );

  const personMonthStats = useMemo(() => buildCffPersonMonthStats(monthRows), [monthRows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Search is global (all months); month filter only applies when the box is empty.
    let list = q ? rows : monthRows;
    if (q) {
      const tokens = q.split(/\s+/).filter(Boolean);
      list = rows.filter((row) => {
        const blob = cffRowSearchBlob(row);
        return tokens.every((token) => blob.includes(token));
      });
    }
    // Newest NCPAR first (application_no descending).
    return [...list].sort((a, b) => b.application_no.localeCompare(a.application_no, undefined, { numeric: true }));
  }, [rows, monthRows, search]);

  const duplicateInvoiceKeys = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of rows) {
      const invoice = (row.form_data as CustomerFeedbackFormData)?.invoice_no?.trim();
      if (!invoice) continue;
      const key = invoice.toLowerCase();
      map.set(key, (map.get(key) ?? 0) + 1);
    }
    return new Set(
      [...map.entries()].filter(([, count]) => count > 1).map(([key]) => key),
    );
  }, [rows]);

  const detailPath = (id: string) => `${listBase}/${id}`;

  const monthEntries = useCallback(
    () =>
      monthRows.map((row) => ({
        form: row.form_data as CustomerFeedbackFormData,
        applicationNo: row.application_no,
      })),
    [monthRows],
  );

  const handleDownloadMonth = async () => {
    if (!monthRows.length) {
      setError(`No CFF forms for ${monthLabel(yearMonth)}.`);
      return;
    }
    setExporting(true);
    setExportOverlay('Preparing Excel…');
    setError(null);
    setNotice(null);
    try {
      await downloadMonthlyCffExcel(monthEntries(), yearMonth);
      setExportOverlay(null);
      setNotice(`Downloaded ${monthRows.length} sheet(s) for ${monthLabel(yearMonth)}.`);
    } catch (err) {
      setExportOverlay(null);
      setError(err instanceof Error ? err.message : 'Failed to export month Excel.');
    } finally {
      setExportOverlay(null);
      setExporting(false);
    }
  };

  const handleDownloadMasterList = async () => {
    if (!monthRows.length) {
      setError(`No CFF forms for ${monthLabel(yearMonth)}.`);
      return;
    }
    setExporting(true);
    setExportOverlay('Preparing master list…');
    setError(null);
    setNotice(null);
    try {
      const listRows = monthRows.map((row) => {
        const data = row.form_data as CustomerFeedbackFormData;
        return {
          applicationNo: row.application_no,
          status: STATUS_LABEL[row.status] ?? row.status,
          companyName: row.company_name?.trim() || '',
          companyCode: data?.company_code?.trim() || '',
          requestType: requestTypeLabel(data?.request_type || ''),
          invoiceNo: data?.invoice_no?.trim() || '',
          itemCodes: itemCodesLabel(data),
          reason: (data?.reason ?? '').trim(),
          internalRemarks: (data?.internal_remarks ?? '').trim(),
          submittedBy: formatCffSubmittedByLabel(
            data?.submitted_by?.trim() || row.submitted_by,
            data?.invoice_issued_by,
          ),
          createdAt: formatSingaporeDateTime(row.created_at || row.submitted_at),
        };
      });
      await downloadCffMasterListExcel(listRows, yearMonth);
      setExportOverlay(null);
      setNotice(`Downloaded master list (${monthRows.length} row(s)) for ${monthLabel(yearMonth)}.`);
    } catch (err) {
      setExportOverlay(null);
      setError(err instanceof Error ? err.message : 'Failed to export master list.');
    } finally {
      setExportOverlay(null);
      setExporting(false);
    }
  };

  const handleSyncMonth = async () => {
    if (!monthRows.length) {
      setError(`No CFF forms for ${monthLabel(yearMonth)}.`);
      return;
    }
    setExporting(true);
    setError(null);
    setNotice(null);
    try {
      setExportOverlay(`Uploading 0/${monthRows.length}…`);
      const { rebuildCffMonthOnSharePoint } = await import('../../lib/applicationFormSettings');
      let ok = 0;
      for (const row of monthRows) {
        await rebuildCffMonthOnSharePoint(row.application_no, {
          submissionId: row.id,
          form: row.form_data as CustomerFeedbackFormData,
        });
        ok += 1;
        setExportOverlay(`Uploading ${ok}/${monthRows.length}…`);
      }
      setExportOverlay(null);
      setNotice(
        `Synced ${ok} file(s) into SharePoint folder mm. mmm'yy (e.g. 08. Aug'26) as CFF-….xlsx.`,
      );
    } catch (err) {
      setExportOverlay(null);
      setError(err instanceof Error ? err.message : 'Failed to sync month to SharePoint.');
      setNotice(null);
    } finally {
      setExportOverlay(null);
      setExporting(false);
    }
  };

  const isDem = kind === 'dem';
  const periodPickers = (
    <CffPeriodPickers
      year={year}
      month={month}
      yearOptions={yearOptions}
      countsByYm={countsByYm}
      disabled={exporting || loading}
      yearAriaLabel="Filter by year"
      monthAriaLabel="Filter by month"
      onYearChange={setYear}
      onMonthChange={setMonth}
    />
  );
  const exportMenu = (
    <ActionMenuButton
      label={exporting ? 'Working…' : 'Export'}
      disabled={exporting || loading || monthRows.length === 0}
      items={[
        {
          id: 'download-master-list',
          label: 'Download master list',
          onSelect: () => {
            void handleDownloadMasterList();
          },
        },
        {
          id: 'download-excel',
          label: 'Download Excel (forms)',
          onSelect: () => {
            void handleDownloadMonth();
          },
        },
        {
          id: 'sync-sharepoint',
          label: 'Sync SharePoint',
          onSelect: () => {
            void handleSyncMonth();
          },
        },
      ]}
    />
  );
  const headerActions = isDem ? (
    <div className="dem-floating-page-actions__btns">
      <button
        type="button"
        className="btn-secondary btn-sm"
        disabled={loading}
        onClick={() => void load()}
      >
        Refresh
      </button>
      <button
        type="button"
        className="btn-secondary btn-sm"
        disabled={loading}
        onClick={() => setStatsOpen(true)}
      >
        Statistics
      </button>
      {exportMenu}
      <Link to={newPath} className="btn-primary">
        New CFF
      </Link>
    </div>
  ) : (
    <div className="cff-page__header-actions">
      <Link to={homePath} className="btn-secondary">
        All forms
      </Link>
      <Link to={newPath} className="btn-primary">
        + New CFF
      </Link>
    </div>
  );

  return (
    <div className={`cff-page${isDem ? ' cff-page--floating dem-list-scroll' : ''}`}>
      {isDem ? (
        <div className="dem-floating-page-actions dem-floating-page-actions--end">{headerActions}</div>
      ) : (
        <PageHeader title="Customer Feedback Form (CFF)" actions={headerActions} />
      )}

      <PageToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by NCPAR, company, invoice, items, reason or remarks"
        leading={
          isDem ? (
            <div className="cff-page__toolbar-aside cff-page__toolbar-aside--leading">
              <div className="cff-page__toolbar-controls">{periodPickers}</div>
            </div>
          ) : undefined
        }
        action={
          isDem ? undefined : (
            <div className="cff-page__toolbar-aside">
              <div className="cff-page__toolbar-controls">
                {periodPickers}
                <span className="cff-page__toolbar-divider" aria-hidden />
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={loading}
                  onClick={() => setStatsOpen(true)}
                >
                  Statistics
                </button>
                {exportMenu}
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={loading}
                  onClick={() => void load()}
                >
                  Refresh
                </button>
              </div>
            </div>
          )
        }
      />

      {exportOverlay ? <VerifyingOverlay message={exportOverlay} /> : null}
      {activeToast
        ? createPortal(
            <PageNotificationToast notification={activeToast} onDismiss={dismissToast} />,
            document.body,
          )
        : null}

      {loading && rows.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__spinner" />
          <p>Loading forms…</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <p>No customer feedback forms yet.</p>
          <Link to={newPath} className="btn-primary btn-sm" style={{ marginTop: '0.75rem' }}>
            {isDem ? 'New CFF' : '+ New CFF'}
          </Link>
        </div>
      ) : monthRows.length === 0 && !search.trim() ? (
        <div className="empty-state">
          <p>No CFF forms for {monthLabel(yearMonth)}.</p>
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="empty-state">
          <p>No forms match your search.</p>
          <button type="button" className="btn-secondary btn-sm" onClick={() => setSearch('')}>
            Clear search
          </button>
        </div>
      ) : (
        <div className="cff-list card">
          <div className="dem-table-scroll">
            <div className="cff-list__head" role="row">
              <span>NCPAR No.</span>
              <span>Company</span>
              <span>Request</span>
              <span>Invoice No.</span>
              <span className="cff-list__head-col--wide">Items No.</span>
              <span className="cff-list__head-col--wide">Reason</span>
              <span className="cff-list__head-col--wide">Internal remarks</span>
              <span>Picker / Packer / Driver</span>
              <span>Submitted by</span>
              <span aria-hidden />
            </div>
            {filteredRows.map((row) => {
              const invoice = (row.form_data as CustomerFeedbackFormData)?.invoice_no?.trim() || '';
              return (
                <CffListRow
                  key={row.id}
                  row={row}
                  detailPath={detailPath(row.id)}
                  duplicateInvoice={
                    invoice.length > 0 && duplicateInvoiceKeys.has(invoice.toLowerCase())
                  }
                />
              );
            })}
          </div>
        </div>
      )}

      <Modal
        open={statsOpen}
        onClose={() => setStatsOpen(false)}
        title="CFF statistics by person"
        wide
        panelClassName="cff-stats-modal"
      >
        <div className="cff-stats">
          <div className="cff-stats__toolbar">
            <CffPeriodPickers
              year={year}
              month={month}
              yearOptions={yearOptions}
              countsByYm={countsByYm}
              yearAriaLabel="Statistics year"
              monthAriaLabel="Statistics month"
              onYearChange={setYear}
              onMonthChange={setMonth}
            />
            <p className="cff-stats__summary">
              <strong>{monthRows.length}</strong> CFF
              {monthRows.length === 1 ? '' : 's'} in {monthLabel(yearMonth)} ·{' '}
              <strong>{personMonthStats.length}</strong> person
              {personMonthStats.length === 1 ? '' : 's'}
            </p>
          </div>

          {personMonthStats.length === 0 ? (
            <p className="cff-stats__empty">No CFF forms for this month.</p>
          ) : (
            <div className="cff-stats__visual" role="list" aria-label="CFF counts by creator">
              {personMonthStats.map((row) => {
                const max = personMonthStats[0]?.total || 1;
                const pct = Math.max(6, Math.round((row.total / max) * 100));
                return (
                  <div key={row.person} className="cff-stats__bar-row" role="listitem">
                    <div className="cff-stats__bar-meta">
                      <span className="cff-stats__person">{row.person}</span>
                      <span className="cff-stats__num cff-stats__num--total">{row.total}</span>
                    </div>
                    <div className="cff-stats__bar-track" aria-hidden>
                      <div className="cff-stats__bar-fill" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
